// ============================================================================
// Chat Compound Component
// ============================================================================
// A context-aware chat UI system built on use-langgraph-stream.
//
// Example:
//   <Chat.Root streamOptions={{ apiUrl, assistantId, threadId }}>
//     <Chat.Messages.List>
//       {messages.map(msg =>
//         msg.role === 'user'
//           ? <Chat.UserMessage key={msg.id} blocks={msg.blocks} />
//           : <Chat.AIMessage.Root key={msg.id}>
//               <Chat.AIMessage.Content>{msg.text}</Chat.AIMessage.Content>
//             </Chat.AIMessage.Root>
//       )}
//       <Chat.Messages.StreamingIndicator />
//       {/* ScrollAnchor is built into List — no need to add manually */}
//     </Chat.Messages.List>
//     <Chat.Input.DropZone>
//       <Chat.Input.AttachmentList />
//       <Chat.Input.Textarea placeholder="Type..." />
//       <Chat.Input.SubmitButton>Send</Chat.Input.SubmitButton>
//     </Chat.Input.DropZone>
//   </Chat.Root>
// ============================================================================

// ChatRoot
export { ChatRoot, type ChatRootProps } from "./ChatRoot";

// Context convenience hooks
export { useChatPersonas, type ChatPersona } from "./ChatContext";
export { MessageLockup, type MessageLockupProps } from "./primitives/MessageLockup";
export {
  useChatMessages,
  useChatTextMessages,
  useChatTurns,
  useChatIsStreaming,
  useChatIsLoading,
  useChatIsLoadingHistory,
  useChatIsReady,
  useChatError,
  useChatStateError,
  useChatStop,
  useChatValues,
  useChatComposer,
  useChatSubmit,
  useChatStreamSelector,
  useChatIsInputDisabled,
  useChatOnDisabledClick,
  useChatInterrupt,
  useChatRespondToInterrupt,
  useChatConcurrentSubmit,
  useChatQueuedMessages,
} from "./ChatContext";

// Utility hooks
export { useMessageMetadata, type MessageMetadata } from "./hooks/index";
export { useOnStreamEnd } from "./hooks/index";
export {
  useScrollAnchor,
  type UseScrollAnchorOptions,
  type UseScrollAnchorReturn,
} from "./hooks/index";
export { useTurns, deriveTurns, getCompletedToolCallIds, type Turn } from "./hooks/index";
export {
  useToolActivity,
  useAgentActivity,
  deriveToolActivityGroups,
  categoryForTool,
  DEFAULT_TOOL_CATEGORIES,
  type ToolActivityGroup,
  type ToolActivityItem,
  type ToolActivityStatus,
  type ToolCategoryName,
  type DeriveToolActivityOptions,
  type AgentActivityState,
  type UseAgentActivityOptions,
} from "./hooks/index";

// Context-aware input components
export {
  Input,
  Textarea,
  SubmitButton,
  FileButton,
  DropZone,
  AttachmentList,
  type TextareaProps,
  type SubmitButtonProps,
  type FileButtonProps,
  type DropZoneProps,
  type AttachmentListProps,
} from "./input/index";

// Context-aware message components
export {
  Messages,
  List,
  StreamingIndicator,
  ScrollAnchor,
  type ListProps,
  type StreamingIndicatorProps,
  type ScrollAnchorProps,
} from "./messages/index";

// Primitives
export {
  UserMessage,
  type UserMessageProps,
  type UserMessageSimpleProps,
  type UserMessageContentProps,
} from "./primitives/UserMessage";
export {
  AIMessage,
  type AIMessageProps,
  type AIMessageRootProps,
  type AIMessageContentProps,
  type AIMessageBubbleProps,
} from "./primitives/AIMessage";
export {
  Suggestions,
  type SuggestionsRootProps,
  type SuggestionsItemProps,
} from "./primitives/Suggestions";
export {
  IntentButtons,
  type IntentButtonsRootProps,
  type IntentButtonProps,
  type IntentButtonVariant,
} from "./primitives/IntentButtons";
export {
  ThinkingIndicator,
  type ThinkingIndicatorProps,
  type ThinkingIndicatorVariant,
} from "./primitives/ThinkingIndicator";
export { ToolCallIndicator, type ToolCallIndicatorProps } from "./primitives/ToolCallIndicator";
export {
  ToolActivity,
  type ToolActivityProps,
  AgentActivity,
  type AgentActivityProps,
} from "./primitives/ToolActivityCard";
export {
  TodoList,
  type TodoListProps,
  type TodoItem,
  type TodoDelegation,
} from "./primitives/TodoList";
export {
  ToolLabelsProvider,
  useToolLabels,
  type ToolLabels,
  type ToolLabelsProviderProps,
} from "./primitives/ToolLabelsContext";
export {
  ErrorBanner,
  type ErrorBannerProps,
  type StateErrorPayload,
  type StateErrorKind,
} from "./primitives/ErrorBanner";
export {
  QueuedMessages,
  type QueuedMessagesProps,
} from "./primitives/QueuedMessages";

// Recommendations — the in-chat recommendation row + composer transform
export {
  Recommendations,
  RecommendationsRoot,
  RecommendationChips,
  RecommendationComposer,
  RecommendationSubmissionMessage,
  GenericFieldsPanel,
  useRecommendations,
  useRecommendationPanel,
  useRecommendationRenderers,
  buildRecommendationSubmission,
  getRecommendationSubmission,
  parseRecommendationFields,
  attachmentTag,
  attachmentTagPrefix,
  type Recommendation,
  type RecommendationFieldSpec,
  type RecommendationCollects,
  type CollectedValue,
  type CollectedValues,
  type ParsedRecommendationField,
  type RecommendationPanelProps,
  type RecommendationSubmissionProps,
  type RecommendationPanelDescriptor,
  type RecommendationPanelRenderers,
  type RecommendationsRootProps,
  type RecommendationChipsProps,
  type RecommendationComposerProps,
  type RecommendationSubmissionMessageProps,
  type RecommendationSubmissionInfo,
  type RecommendationSubmissionInput,
  type RecommendationSubmissionPayload,
} from "./recommendations/index";

// Attachment components
export {
  ImageThumbnail,
  FilePill,
  MessageImages,
  MessageDocuments,
  BaseAttachmentList,
  BaseDropZone,
  toDisplayAttachment,
  fileToAttachment,
  filesToAttachments,
  extractText,
  extractImageUrls,
  type ChatAttachment,
  type DocumentAttachment,
  type MessageContent,
  type MessageContentComplex,
  type MessageContentText,
  type MessageContentImageUrl,
} from "./attachments/index";

// ============================================================================
// Compound Component Object
// ============================================================================

import { ChatRoot } from "./ChatRoot";
import { Input } from "./input/index";
import { Messages } from "./messages/index";
import { UserMessage } from "./primitives/UserMessage";
import { AIMessage } from "./primitives/AIMessage";
import { Suggestions } from "./primitives/Suggestions";
import { IntentButtons } from "./primitives/IntentButtons";
import { ThinkingIndicator } from "./primitives/ThinkingIndicator";
import { ToolCallIndicator } from "./primitives/ToolCallIndicator";
import { ToolActivity, AgentActivity } from "./primitives/ToolActivityCard";
import { TodoList } from "./primitives/TodoList";
import { ErrorBanner } from "./primitives/ErrorBanner";
import { QueuedMessages } from "./primitives/QueuedMessages";
import { Recommendations } from "./recommendations/index";
import { RecommendationChips } from "./recommendations/index";

export const Chat = {
  Root: ChatRoot,
  Input,
  Messages,
  ErrorBanner,
  UserMessage,
  AIMessage,
  Suggestions,
  IntentButtons,
  ThinkingIndicator,
  ToolCallIndicator,
  ToolActivity,
  AgentActivity,
  TodoList,
  Recommendations,
  RecommendationChips,
  QueuedMessages,
};
